'use client'

import { toast, useDocumentInfo, useFormFields } from '@payloadcms/ui'
import { IconCheck, IconX } from '@tabler/icons-react'
import { useRouter } from 'next/navigation'

export const MentorCheckButtons = ({ path }: { path?: string }) => {
  const router = useRouter()
  const { id: documentId } = useDocumentInfo()

  // Remove the '.buttons' suffix from path to get the array item path
  // e.g., "mentorCheckNodes.2.buttons" -> "mentorCheckNodes.2"
  const arrayItemPath = path?.replace(/\.buttons$/, '') || ''

  // Build the correct paths for array item fields
  const nodePath = arrayItemPath ? `${arrayItemPath}.node` : 'node'
  const statusPath = arrayItemPath ? `${arrayItemPath}.status` : 'status'

  // Get form field values using the correct paths
  const node = useFormFields(([fields]) => {
    return fields[nodePath]?.value
  })

  const status = useFormFields(([fields]) => {
    return fields[statusPath]?.value
  })

  // Get student and skillTree from the parent document
  const student = useFormFields(([fields]) => {
    return fields['student']?.value
  })

  const skillTree = useFormFields(([fields]) => {
    return fields['skillTree']?.value
  })

  console.log('MentorCheckButtons debug:', {
    path,
    arrayItemPath,
    nodePath,
    statusPath,
    node,
    status,
    student,
    skillTree,
    documentId,
  })

  // Only show buttons if status is 'submitted'
  if (status !== 'submitted') {
    return null
  }

  // Extract the actual IDs
  const skillTreeNodeId = typeof node === 'object' ? (node as any)?.id : node
  const studentId = typeof student === 'object' ? (student as any)?.id : student
  const skillTreeId = typeof skillTree === 'object' ? (skillTree as any)?.id : skillTree

  if (!skillTreeNodeId || !studentId || !skillTreeId) {
    return null
  }

  const handleMentorAction = async (action: 'approve' | 'request-revision') => {
    const actionPromise = fetch(
      `/api/student-progress/students/${studentId}/skill-trees/${skillTreeId}/nodes/${skillTreeNodeId}/mentor-approve`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action,
        }),
      },
    ).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }
      return response.json()
    })

    toast.promise(actionPromise, {
      loading: action === 'approve' ? 'Approving...' : 'Requesting revision...',
      success: () => {
        // Refresh the page to show updated data
        setTimeout(() => router.refresh(), 500)
        return action === 'approve'
          ? 'Mentor check approved successfully'
          : 'Revision requested successfully'
      },
      error: (error) => {
        console.error('Error updating mentor check:', error)
        return `Failed to ${action === 'approve' ? 'approve' : 'request revision'}: ${error instanceof Error ? error.message : 'Unknown error'}`
      },
    })
  }

  return (
    <div className="flex gap-3 p-3 bg-[var(--theme-elevation-50)] border border-[var(--theme-elevation-100)] rounded-md mt-2 mb-4">
      <button
        type="button"
        onClick={() => handleMentorAction('approve')}
        className="inline-flex items-center px-4 py-2 text-sm font-medium text-green-700 bg-green-100 border border-green-300 rounded-md cursor-pointer gap-1.5 transition-all hover:bg-green-200 hover:-translate-y-0.5 hover:shadow-sm"
      >
        <IconCheck size={18} />
        Approve Submission
      </button>

      <button
        type="button"
        onClick={() => handleMentorAction('request-revision')}
        className="inline-flex items-center px-4 py-2 text-sm font-medium text-red-700 bg-red-100 border border-red-300 rounded-md cursor-pointer gap-1.5 transition-all hover:bg-red-200 hover:-translate-y-0.5 hover:shadow-sm"
      >
        <IconX size={18} />
        Request Revision
      </button>
    </div>
  )
}
